/*
 * Turns an API failure into something a person can act on.
 *
 * Two exports, on purpose:
 *
 *   describeError(err, fallback) -> { severity, title, detail, fields, action, ... }
 *   getErrorMessage(err, fallback) -> string        (legacy, ~30 call sites)
 *
 * `getErrorMessage` is built on top of `describeError` and deliberately keeps
 * the OLD precedence rules, so the thirty screens still importing it render the
 * same sentence they rendered before. Screens move to `describeError` one at a
 * time; rewriting every call site in one change would have been unreviewable,
 * and any wording regression would have been invisible in the diff.
 *
 * ── Body shapes we parse ──────────────────────────────────────────────────
 * The API is not consistent about how it reports failures, and a backend
 * standardisation is in flight, so we keep parsing everything the DEPLOYED
 * backend still emits. Old clients must not break the day a new shape lands,
 * and this client must not break against a server that has not been redeployed:
 *
 *   {"email":["The email field is required."]}     bare validator bag
 *   "{\"email\":[\"...\"]}"                         ->toJson(), a JSON *string*
 *   {"errors":{"email":["..."]}}                    wrapped bag
 *   {"error":"Invalid credentials"}                 single message
 *   {"message":"2FA code required"}                 single message
 *   "Rate limit exceeded"                           plain-text body
 *
 * A new field-keyed shape slots in by adding one entry to FIELD_EXTRACTORS.
 * The two forms the backend is most likely to land on (`{fields:{...}}` and
 * `{errors:[{field,message}]}`) are already listed there, inert until the
 * server actually starts sending them.
 *
 * Anything we cannot interpret falls back to friendly wording rather than
 * leaking a raw payload ("[object Object]", a JSON blob, an HTML error page).
 */

const MAX_DETAIL_LENGTH = 300;
const MAX_TITLE_LENGTH = 90;

// Above this a server sentence is too long to be a headline and becomes the
// second line instead. Roughly one wrapped line at the toast's width.
const TITLE_FROM_MESSAGE_LIMIT = 72;

const isFilledString = (v) => typeof v === "string" && v.trim().length > 0;

const truncate = (value, max) =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

// Laravel's ->toJson() double-encodes: the body is a JSON string of JSON.
const parseIfJsonString = (value) => {
  if (!isFilledString(value)) return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

/* ── Field bags ─────────────────────────────────────────────────────────── */

// A validator bag is an object whose values are all strings or arrays of them.
// Normalised to { field: "joined messages" } so no consumer has to care whether
// the server sent one string or an array of them.
const collectBag = (bag) => {
  if (!bag || typeof bag !== "object" || Array.isArray(bag)) return null;

  const entries = Object.entries(bag);
  if (entries.length === 0) return null;

  const out = {};
  for (const [field, value] of entries) {
    const items = Array.isArray(value) ? value : [value];
    const messages = [];
    for (const item of items) {
      if (!isFilledString(item)) return null; // not a validator bag
      messages.push(item.trim());
    }
    out[field] = messages.join(" ");
  }
  return out;
};

// `[{ field: "email", message: "..." }]` — a shape the backend standardisation
// may land on. Inert until it does.
const collectFieldList = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;

  const out = {};
  for (const entry of list) {
    if (!entry || typeof entry !== "object") return null;
    const field = entry.field ?? entry.name ?? entry.path;
    const message = entry.message ?? entry.detail ?? entry.error;
    if (!isFilledString(field) || !isFilledString(message)) return null;
    out[field.trim()] = message.trim();
  }
  return Object.keys(out).length ? out : null;
};

// Keys that belong to the response envelope rather than to a form.
const ENVELOPE_KEYS = new Set([
  "message",
  "error",
  "errors",
  "fields",
  "success",
  "status",
  "code",
  "data",
]);

/*
 * Ordered: the first extractor that recognises the body wins. Adding a new
 * server shape is one entry here and nothing else in this file changes.
 */
const FIELD_EXTRACTORS = [
  // {"errors":{"email":["..."]}} — also the ->toJson() string form.
  (data) => collectBag(parseIfJsonString(data.errors) || data.errors),
  // {"errors":[{"field":"email","message":"..."}]} — future shape.
  (data) => collectFieldList(parseIfJsonString(data.errors) || data.errors),
  // {"fields":{"email":"..."}} — future shape.
  (data) => collectBag(parseIfJsonString(data.fields) || data.fields),
  // {"error":{"fields":{...}}} — future shape, envelope form.
  (data) =>
    data.error && typeof data.error === "object"
      ? collectBag(data.error.fields) || collectFieldList(data.error.fields)
      : null,
  // The whole body IS the bag. Last, because it matches almost any object —
  // running it earlier would read {"message":"..."} as a field called "message".
  // Envelope keys are refused outright so `{errors:"Rate limited"}` does not
  // turn into a form field named "errors" and a title of "Check the errors field".
  (data) => (Object.keys(data).some((key) => ENVELOPE_KEYS.has(key)) ? null : collectBag(data)),
];

const extractFields = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;

  // An envelope key is never a form field, so when one is present the
  // whole-body extractor is skipped rather than allowed to misread it.
  const extractors = isFilledString(data.message) || data.error
    ? FIELD_EXTRACTORS.slice(0, -1)
    : FIELD_EXTRACTORS;

  for (const extract of extractors) {
    const found = extract(data);
    if (found) return found;
  }
  return null;
};

/* ── Single message ─────────────────────────────────────────────────────── */

/*
 * Precedence is load-bearing: `message`, then `error`, then the bag. It is the
 * order the previous implementation used and `getErrorMessage` still returns
 * this value verbatim, so changing it would silently reword thirty screens.
 */
const extractMessage = (data) => {
  if (data === null || data === undefined) return null;

  if (typeof data === "string") {
    const parsed = parseIfJsonString(data);
    if (parsed) return extractMessage(parsed);
    // A plain string body is a message — unless it's an HTML error page.
    const trimmed = data.trim();
    return trimmed.startsWith("<") ? null : trimmed || null;
  }

  if (typeof data !== "object" || Array.isArray(data)) return null;

  if (isFilledString(data.message)) return data.message.trim();
  if (isFilledString(data.error)) return data.error.trim();
  if (data.error && typeof data.error === "object" && isFilledString(data.error.message)) {
    return data.error.message.trim();
  }

  const fields = extractFields(data);
  if (fields) return Object.values(fields).join(" ");

  if (isFilledString(data.errors)) return data.errors.trim();

  return null;
};

/* ── Retry-After ────────────────────────────────────────────────────────── */

const formatSeconds = (seconds) => {
  const s = Math.max(1, Math.round(seconds));
  if (s < 60) return `${s} second${s === 1 ? "" : "s"}`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"}`;
  const h = Math.round(m / 60);
  return `${h} hour${h === 1 ? "" : "s"}`;
};

// A 429 can carry the window as a header or in the body, as a seconds count or
// an HTTP date. Read all of them: "wait 3 minutes" versus "wait a moment" is
// the whole difference between an actionable message and a shrug.
const readRetryAfter = (response) => {
  const headers = response?.headers || {};
  const candidates = [
    headers["retry-after"],
    headers["Retry-After"],
    response?.data?.retry_after,
    response?.data?.retryAfter,
    response?.data?.retry_after_seconds,
  ];

  for (const raw of candidates) {
    if (raw === null || raw === undefined || raw === "") continue;

    const asNumber = Number(raw);
    if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;

    const asDate = Date.parse(raw);
    if (Number.isFinite(asDate)) {
      const delta = (asDate - Date.now()) / 1000;
      if (delta > 0) return delta;
    }
  }
  return null;
};

/* ── Status → wording ───────────────────────────────────────────────────── */

/*
 * `soft: true` means "do not let this wording override the caller's fallback in
 * the legacy string export".
 *
 * The case that forces it: a failed login is a 401, and Login.vue's fallback is
 * "Login failed. Please check your credentials and try again." If 401 were a
 * hard mapping, every wrong password would read "Your session has expired",
 * which is simply untrue at a login form. `describeError` still returns the
 * session wording — a toast on an authenticated screen wants exactly that — but
 * a caller that already wrote a better sentence keeps it.
 */
const STATUS_WORDING = {
  400: {
    severity: "error",
    title: "That request wasn't accepted",
    detail: "Check the details you entered and try again.",
    soft: true,
  },
  401: {
    severity: "warning",
    title: "Your session has expired",
    detail: "Sign in again to pick up where you left off.",
    action: "signin",
    soft: true,
  },
  402: {
    severity: "error",
    title: "Payment required",
    detail: "This needs an active plan or more tokens on your account.",
    soft: true,
  },
  403: {
    severity: "error",
    title: "You're not allowed to do that",
    detail: "Your account doesn't have permission for this action.",
    action: "support",
  },
  404: {
    severity: "error",
    title: "That's gone or moved",
    detail: "We couldn't find what you were looking for. It may have been deleted.",
  },
  409: {
    severity: "error",
    title: "This changed while you were working",
    detail: "Something else updated it first. Reload and try again.",
    action: "reload",
    soft: true,
  },
  413: {
    severity: "error",
    title: "That's too large",
    detail: "Send a smaller file and try again.",
  },
  422: {
    severity: "error",
    title: "Some details need fixing",
    detail: "Check the highlighted fields and try again.",
    soft: true,
  },
  429: {
    severity: "warning",
    title: "Too many attempts",
    detail: "Please wait a moment and try again.",
  },
};

const SERVER_WORDING = {
  severity: "error",
  title: "That one's on us",
  detail: "The server ran into a problem. Please try again shortly.",
};

const OFFLINE_WORDING = {
  severity: "error",
  title: "No connection",
  detail: "We couldn't reach the server. Check your internet connection and try again.",
};

const TIMEOUT_WORDING = {
  severity: "error",
  title: "That took too long",
  detail: "The server didn't respond in time. Please try again.",
};

/* ── Built-in actions ───────────────────────────────────────────────────── */

/*
 * Plain `window` navigation rather than the router: this module is imported by
 * services and by the Vuex store, where no router instance is in scope. A full
 * page load after a session expiry is what we want anyway — it clears every
 * stale piece of in-memory state along with the dead token.
 *
 * 403 sends people to support, not to a settings screen: this app has no
 * permissions UI, so "Go to settings" would be a dead end — a button that
 * cannot fix the thing it is offered for is worse than no button.
 */
const BUILT_IN_ACTIONS = {
  signin: {
    label: "Sign in again",
    onClick: () => window.location.assign("/login"),
  },
  reload: {
    label: "Reload",
    onClick: () => window.location.reload(),
  },
  support: {
    label: "Contact support",
    onClick: () => window.location.assign("/support"),
  },
};

/* ── Field names ────────────────────────────────────────────────────────── */

const humanizeField = (field) =>
  String(field)
    .replace(/[_.-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();

/* ── Public API ─────────────────────────────────────────────────────────── */

const DEFAULT_FALLBACK = "Something went wrong. Please try again.";

const normalizeFallback = (fallback) => {
  if (isFilledString(fallback)) return { title: fallback.trim(), detail: "" };
  if (fallback && typeof fallback === "object") {
    return {
      title: isFilledString(fallback.title) ? fallback.title.trim() : "",
      detail: isFilledString(fallback.detail) ? fallback.detail.trim() : "",
    };
  }
  return { title: "", detail: "" };
};

function build(parts) {
  const title = truncate((parts.title || DEFAULT_FALLBACK).trim(), MAX_TITLE_LENGTH);
  const rawDetail = isFilledString(parts.detail)
    ? truncate(parts.detail.trim(), MAX_DETAIL_LENGTH)
    : "";

  return {
    severity: parts.severity || "error",
    title,
    // Never say the same thing twice on two lines.
    detail: rawDetail === title ? "" : rawDetail,
    fields: parts.fields || null,
    action: parts.action || null,
    status: parts.status ?? null,
    retryAfter: parts.retryAfter ?? null,
    message: parts.message ?? null,
    /*
     * `detail` prefers the server's own sentence, which is right for a toast —
     * it is usually the most specific thing anyone knows. It is wrong for an
     * inline panel, where that sentence is written for a developer ("User must
     * reauthenticate") and sits under a heading a customer is meant to act on.
     * `ownDetail` is always OUR wording, so a panel can show that and demote
     * the server's to a details disclosure. Falls back to `detail` when we have
     * no wording of our own, so a caller reading it is never left with nothing.
     */
    ownDetail: (() => {
      const own = isFilledString(parts.ownDetail)
        ? truncate(parts.ownDetail.trim(), MAX_DETAIL_LENGTH)
        : "";
      const chosen = own || rawDetail;
      return chosen === title ? "" : chosen;
    })(),
    legacyMessage: truncate((parts.legacyMessage || title).trim(), MAX_DETAIL_LENGTH),
  };
}

/**
 * Describe a failure as structure rather than as one flattened sentence.
 *
 * @param {*} error an axios error, an Error, a `{ response }` envelope, or a string
 * @param {string|{title:string,detail?:string}} fallback wording for when we can do no better
 * @returns {{
 *   severity: 'error'|'warning',
 *   title: string,
 *   detail: string,
 *   ownDetail: string,
 *   fields: Object<string,string>|null,
 *   action: {label:string,onClick:Function}|null,
 *   status: number|null,
 *   retryAfter: number|null,
 *   message: string|null,
 *   legacyMessage: string
 * }}
 */
export function describeError(error, fallback = DEFAULT_FALLBACK) {
  const fb = normalizeFallback(fallback);
  const fallbackTitle = fb.title || DEFAULT_FALLBACK;

  const response = error?.response;

  /* ── The request never reached the API (offline, DNS, CORS, server down). */
  if (!response) {
    let wording = null;

    if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
      wording = TIMEOUT_WORDING;
    } else if (error?.request) {
      wording = OFFLINE_WORDING;
    } else if (typeof navigator !== "undefined" && navigator.onLine === false) {
      wording = OFFLINE_WORDING;
    }

    if (wording) {
      return build({
        severity: wording.severity,
        title: wording.title,
        detail: wording.detail,
        legacyMessage: wording.detail,
      });
    }

    // Not an HTTP failure at all — a thrown Error, or a bare string.
    const thrown = isFilledString(error)
      ? error.trim()
      : isFilledString(error?.message)
        ? error.message.trim()
        : null;

    if (thrown) {
      return thrown.length <= TITLE_FROM_MESSAGE_LIMIT
        ? build({ title: thrown, detail: fb.detail, legacyMessage: thrown })
        : build({ title: fallbackTitle, detail: thrown, legacyMessage: thrown });
    }

    return build({ title: fallbackTitle, detail: fb.detail, legacyMessage: fallbackTitle });
  }

  /* ── An HTTP response came back. */
  const status = Number(response.status) || null;

  // Unwrap ->toJson() once here so the field extractors, which only understand
  // objects, see the same body the message extractor does. Without this a
  // double-encoded validator bag lost its field names and fell back to the
  // generic "Some details need fixing".
  const data = parseIfJsonString(response.data) || response.data;

  const message = extractMessage(data);
  const fields = extractFields(data);
  const retryAfter = status === 429 ? readRetryAfter({ headers: response.headers, data }) : null;

  const wording =
    STATUS_WORDING[status] || (status && status >= 500 ? SERVER_WORDING : null);

  // A 422 (or a 400 carrying a bag) can name the offending fields, which beats
  // any generic sentence we could write and lets the caller focus the input.
  if (fields && (status === 422 || status === 400)) {
    const names = Object.keys(fields);
    const title =
      names.length === 1
        ? `Check the ${humanizeField(names[0])} field`
        : `Check ${names.length} fields`;

    return build({
      severity: "error",
      title,
      detail: message || Object.values(fields).join(" "),
      fields,
      status,
      message,
      // Old behaviour for 422: the joined bag, else the caller's fallback.
      legacyMessage: message || fallbackTitle,
    });
  }

  if (wording) {
    let detail = message || wording.detail;
    if (status === 429 && retryAfter && !message) {
      detail = `Please wait ${formatSeconds(retryAfter)} and try again.`;
    }
    if (status >= 500 && !message) {
      detail = `The server ran into a problem (error ${status}). Please try again shortly.`;
    }

    return build({
      severity: wording.severity,
      title: wording.title,
      detail,
      ownDetail: detail === message ? wording.detail : detail,
      fields,
      action: BUILT_IN_ACTIONS[wording.action] || null,
      status,
      retryAfter,
      message,
      // `soft` entries never beat the sentence the caller wrote — see the note
      // on STATUS_WORDING.
      legacyMessage: message || (wording.soft ? fallbackTitle : detail),
    });
  }

  // No mapping for this status. A short server sentence makes a better headline
  // than our generic one; a long one belongs on the second line.
  if (message) {
    return message.length <= TITLE_FROM_MESSAGE_LIMIT
      ? build({ title: message, detail: fb.detail, fields, status, message, legacyMessage: message })
      : build({ title: fallbackTitle, detail: message, fields, status, message, legacyMessage: message });
  }

  return build({
    title: fallbackTitle,
    detail: fb.detail,
    fields,
    status,
    legacyMessage: fallbackTitle,
  });
}

/**
 * Legacy single-string form. Unchanged behaviour for every existing caller.
 *
 * Kept rather than codemodded away because ~30 screens render it into their own
 * inline error panels, not into a toast. Those panels want one sentence, and
 * rewriting all of them in the same change would have buried any wording
 * regression in an unreadable diff.
 */
export function getErrorMessage(error, fallback = DEFAULT_FALLBACK) {
  return describeError(error, fallback).legacyMessage;
}

export default getErrorMessage;
